import {
  RulerHorizontalIcon,
  StretchHorizontallyIcon,
} from '@radix-ui/react-icons'
import { useGeckoAnalytics } from 'features/analytics'
import { useKestrel } from 'features/kestrelViewer'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { KestrelViewerTool } from './KestrelViewerTool'

const MeasurementModesContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const SubModesContainer = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  left: 100%;
  top: 0;
  display: flex;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  pointer-events: ${({ $isVisible }) => ($isVisible ? 'auto' : 'none')};
  transition: opacity 0.2s ease-in-out;
`

export const MeasurementModes: React.FC = () => {
  const {
    sendCommand,
    selectedMode,
    setSelectedMode,
    measurementType,
    setMeasurementType,
  } = useKestrelViewerContext()
  const { trackButtonClicked } = useGeckoAnalytics()
  const { kestrelModel, gatekeeper } = useKestrel()
  const [showSubModes, setShowSubModes] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleMouseLeave = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.relatedTarget as Node)
      ) {
        setShowSubModes(false)
      }
    }

    const container = containerRef.current
    if (container) {
      container.addEventListener('mouseleave', handleMouseLeave)
    }

    return () => {
      if (container) {
        container.removeEventListener('mouseleave', handleMouseLeave)
      }
    }
  }, [])

  const handleModeChange = (mode: string) => {
    if (!gatekeeper.feature.allowThicknessMeasurement && mode === 'thickness')
      return
    setSelectedMode('measure')
    setMeasurementType(mode)
    sendCommand({
      command: KestrelCommands.TOGGLE_MEASUREMENT_TYPE,
      measurementType: mode,
    })
    trackButtonClicked({
      button_name: `kestrel_set_measurement_mode`,
      kestrel_model_id: kestrelModel?.data.kestrel_model_id,
      measurement_type: mode,
    })
  }

  // Enforce straight line measurement if thickness is disallowed
  useEffect(() => {
    if (
      !gatekeeper.feature.allowThicknessMeasurement &&
      measurementType === 'thickness'
    ) {
      setSelectedMode('measure')
      setMeasurementType('straightLine')
      sendCommand({
        command: KestrelCommands.TOGGLE_MEASUREMENT_TYPE,
        measurementType: 'straightLine',
      })
    }
  }, [
    measurementType,
    sendCommand,
    setMeasurementType,
    setSelectedMode,
    gatekeeper.feature.allowThicknessMeasurement,
  ])

  return (
    <MeasurementModesContainer
      ref={containerRef}
      onMouseEnter={() => setShowSubModes(true)}>
      <KestrelViewerTool
        tooltipLabel={
          measurementType === 'straightLine'
            ? 'Measure 2 points'
            : 'Measure thickness'
        }
        tooltipSide='top'
        modeId='measure'
        selectedMode={selectedMode}
        measurementTypeId={measurementType}>
        {measurementType === 'straightLine' ? (
          <RulerHorizontalIcon width={25} height={25} />
        ) : (
          <StretchHorizontallyIcon width={25} height={25} />
        )}
      </KestrelViewerTool>

      <SubModesContainer $isVisible={showSubModes}>
        {measurementType !== 'straightLine' && (
          <KestrelViewerTool
            tooltipLabel={'Measure 2 points'}
            tooltipSide='top'
            modeId='measure'
            selectedMode={selectedMode}
            measurementTypeId='straightLine'
            onClick={() => handleModeChange('straightLine')}>
            <RulerHorizontalIcon width={25} height={25} />
          </KestrelViewerTool>
        )}
        {measurementType !== 'thickness' &&
          gatekeeper.feature.allowThicknessMeasurement && (
            <KestrelViewerTool
              tooltipLabel={'Measure thickness'}
              tooltipSide='top'
              modeId='measure'
              selectedMode={selectedMode}
              measurementTypeId='thickness'
              onClick={() => handleModeChange('thickness')}>
              <StretchHorizontallyIcon width={25} height={25} />
            </KestrelViewerTool>
          )}
      </SubModesContainer>
    </MeasurementModesContainer>
  )
}
